<template>
  <div class="features-list-page">
    <VdPage title="Metrics" :loading="loading">
      <template #actions>
        <Button
          label="Link new metric"
          :disabled="!inWIPVersion"
          class="new-metric-button"
          icon="pi pi-link"
          @click="toggleNewMetricPanel"
        />
        <Popover ref="newMetricOptions" class="metadataOptionsOverlayPanel">
          <div :class="$style.overlayOption" @click="handleCreateVoodooSauceMetric">VoodooSauce Metric</div>
          <div :class="$style.overlayOption" @click="handleCreatePaymentMetric">Payment Metric</div>
        </Popover>
        <InputText
          ref="searchInputRef"
          v-model="searchTerm"
          class="search-input"
          placeholder="Looking for something..."
          data-cy="search-input"
          @focusout="forceFocusOnSearchInput"
        />
      </template>

      <FeaturesListTable
        :game-id="props.gameId"
        :version-id="props.versionId"
        :is-wip="inWIPVersion"
        :is-loading="queryFeaturesLoading"
        :search-term="searchTerm"
        :data="customFeatures"
        feature-source="custom"
        @on-feature-delete="onFeatureDelete"
      />
      <FeaturesListTable
        v-if="externalFeatures?.length !== 0"
        :game-id="props.gameId"
        :version-id="props.versionId"
        :is-wip="inWIPVersion"
        :is-loading="queryFeaturesLoading"
        :search-term="searchTerm"
        :data="externalFeatures"
        feature-source="external"
        :show-extra-actions="false"
      />
      <FeaturesListTable
        :game-id="props.gameId"
        :version-id="props.versionId"
        :is-wip="inWIPVersion"
        :is-loading="queryFeaturesLoading"
        :search-term="searchTerm"
        :data="featuresBySource.system || []"
        feature-source="system"
        @on-feature-delete="onFeatureDelete"
      />
    </VdPage>

    <!-- warning delete dialog -->
    <vd-dialog
      :visible="displayDeleteWarning"
      title="This feature can't be deleted"
      type="error"
      @close="closeDeleteWarning"
    >
      <span>
        You can't delete this feature as it is used
        <strong>in the following segments:</strong>
      </span>
      <ul class="delete-warning-list">
        <li v-for="segment in deleteWarningDetails.segments" :key="segment">
          {{ segment }}
        </li>
      </ul>
      <template #footer>
        <vd-button type="secondary" skin="blue" @click="closeDeleteWarning">I understand</vd-button>
      </template>
    </vd-dialog>
  </div>
</template>

<script setup lang="ts">
/**
 * This page represents the features list.
 * Each feature has a menu with quick actions that allows to edit, delete and export to catalog.
 */

import { VdPage } from "@voodoo.io/voodoo-ui";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Popover from "primevue/popover";
import { computed, reactive, ref } from "vue";
import { useRouter } from "vue-router";

import { queryNames, useDeleteFeatureMutation, useFeatureListPageQuery, useMutationResponse } from "@/graphql";
import useMessages from "@/hooks/messages";
import useSearchFilter from "@/hooks/search-filter";
import links from "@/router/links";
import FeaturesListTable from "./components/FeaturesListTable.vue";

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },
});

const router = useRouter();

const { confirm } = useMessages();

const searchInputRef = ref();

const forceFocusOnSearchInput = () => {
  if (searchInputRef.value) {
    searchInputRef.value.$el.focus();
  }
};

const newMetricOptions = ref();

const toggleNewMetricPanel = (event: MouseEvent) => {
  newMetricOptions.value.toggle(event);
};

/**
 * Load the game features
 */
const { result, loading: queryFeaturesLoading } = useFeatureListPageQuery(
  { gameId: props.gameId, versionId: props.versionId },
  { fetchPolicy: "network-only" }
);

const features = computed(() => result.value?.game?.version?.features || []);

const inWIPVersion = computed(() => result.value?.game?.version?.isWip || false);
/**
 * Filter the features from the searchTerm
 */
const { searchTerm, filteredItems: filteredFeatures } = useSearchFilter(features, ["name", "id", "description"]);

const featuresBySource = computed(() => Object.groupBy(filteredFeatures.value, (feature) => feature.source));

const customFeatures = computed(() => [
  ...(featuresBySource.value.app_custom || []),
  ...(featuresBySource.value.payment_custom || []),
]);

const externalFeatures = computed(() => [...(featuresBySource.value.external || [])]);
/**
 *  Delete a feature
 */
const { mutate: deleteFeature, loading: deleteFeatureLoading } = useDeleteFeatureMutation({
  refetchQueries: [queryNames.FeatureListPage],
});

/**
 * If the feature is used in a segment,
 * we cannot delete it without corrupting the data integrity.
 * We therefore display an information dialog that explain to the user
 * that he has to remove the feature from those resource before continuing.
 */
const deleteWarningDetails = reactive<{ segments: string[] }>({
  segments: [],
});

const displayDeleteWarning = ref(false);

const onFeatureDelete = async (featureId: string) => {
  const confirmed = await confirm("Do you really want to delete this feature?");
  if (confirmed) {
    const response = await deleteFeature({ gameId: props.gameId, versionId: props.versionId, featureId });
    const deleteResult = useMutationResponse(response, (resp) => resp?.deleteFeature);
    if (deleteResult?.isCurrentlyUsed) {
      deleteWarningDetails.segments = deleteResult?.usedInSegments ?? [];
      displayDeleteWarning.value = true;
    }
  }
};

const closeDeleteWarning = () => {
  displayDeleteWarning.value = false;
};

/**
 * The loading status depends on three different loader
 */
const loading = computed(
  () => queryFeaturesLoading.value || deleteFeatureLoading.value /* || exportToCatalogLoading.value */
);

const handleCreateVoodooSauceMetric = () => {
  router.push({
    name: links.games.features.create,
    params: { gameId: props.gameId, versionId: props.versionId },
  });
};

const handleCreatePaymentMetric = () => {
  router.push({
    name: links.games.features.createPayment,
    params: { gameId: props.gameId, versionId: props.versionId },
  });
};
</script>

<style lang="scss" scoped>
.features-list-page {
  .new-metric-button {
    white-space: nowrap;
    width: 160px;
    height: 32px;
    padding: 0.75rem;
  }
  .delete-warning-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    max-height: 300px;
  }
}
</style>

<style module lang="scss">
.overlayOption {
  padding: 1rem;
  cursor: pointer;

  &:hover {
    background-color: var(--vd-color-text-light-9);
  }
}
</style>
